import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Heart, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const BookmarksPage = () => {
  const navigate = useNavigate();
  const [bookmarks, setBookmarks] = useState([]);
  const [devotionals, setDevotionals] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBookmarks();
  }, []);

  const loadBookmarks = async () => {
    try {
      const [bookmarksRes, devotionalsRes] = await Promise.all([
        axios.get(`${API}/bookmarks`, { withCredentials: true }),
        axios.get(`${API}/devotionals`, { withCredentials: true })
      ]);

      setBookmarks(bookmarksRes.data);
      
      const devMap = {};
      devotionalsRes.data.forEach(dev => {
        devMap[dev.devotional_id] = dev;
      });
      setDevotionals(devMap);
    } catch (error) {
      toast.error('Failed to load bookmarks');
    } finally {
      setLoading(false);
    }
  };

  const removeBookmark = async (devotionalId) => {
    try {
      await axios.delete(`${API}/bookmarks/${devotionalId}`, { withCredentials: true });
      setBookmarks(bookmarks.filter(b => b.devotional_id !== devotionalId));
      toast.success('Bookmark removed');
    } catch (error) {
      toast.error('Failed to remove bookmark');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Loading bookmarks...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-4xl" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="bookmarks-title">
            Saved Devotions
          </h1>
          <Button variant="outline" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            Back
          </Button>
        </div>

        {bookmarks.length === 0 ? (
          <div className="text-center py-16" data-testid="empty-bookmarks">
            <Heart className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" strokeWidth={1.5} />
            <p className="text-muted-foreground mb-4">No saved devotions yet</p>
            <Button onClick={() => navigate('/dashboard')} data-testid="browse-btn">
              Browse Devotionals
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="bookmarks-list">
            {bookmarks.map((bookmark) => {
              const devotional = devotionals[bookmark.devotional_id];
              if (!devotional) return null;

              return (
                <Card 
                  key={bookmark.bookmark_id} 
                  className="p-6 border border-primary/10"
                  data-testid={`bookmark-${bookmark.bookmark_id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <p className="text-sm text-muted-foreground mb-2">{devotional.date}</p>
                      <h3 className="text-xl mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {devotional.liturgical_day}
                      </h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        {devotional.season} • Day {devotional.day_number}
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/dashboard?day=${devotional.day_number}`)}
                        data-testid="view-devotional-btn"
                      >
                        View Devotional
                      </Button>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeBookmark(devotional.devotional_id)}
                      data-testid="remove-bookmark-btn"
                    >
                      <Trash2 className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default BookmarksPage;